import { useQuery } from 'react-query';
import { useJwtToken, API_ENDPOINT } from 'helper';
import {
  getDeviceType,
  verifySingleStack,
  getDayTimestamps,
  useGetIDsFromURL,
} from 'components/DeviceDetails/helper';
import dayjs from 'dayjs';

//means no automatic data-refetching
export const REACT_QUERY_LOCAL_DEFAULT_OPTIONS = {
  refetchInterval: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

export const fetchDevice = async (accessToken, Thing_ID) => {
  const url = new URL(`${API_ENDPOINT}/devices/${Thing_ID}`);
  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: accessToken,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching device:', error);
    throw error;
  }
};

export const updateDevice = async (accessToken, payload) => {
  const Thing_ID = payload?.Thing_ID;
  if (!payload || !Thing_ID) {
    throw new Error('Thing_ID required to update device.');
  }

  const url = new URL(`${API_ENDPOINT}/devices/${Thing_ID}`);
  try {
    const updatedDeviceResponse = await fetch(url.toString(), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: accessToken,
      },
      body: JSON.stringify(payload),
    });

    if (!updatedDeviceResponse.ok) {
      const error = await updatedDeviceResponse.json();
      throw new Error(
        `HTTP error! status: ${updatedDeviceResponse.status} - ${error.message}`
      );
    }

    return await updatedDeviceResponse.json();
  } catch (error) {
    console.error('Error updating device:', error);
    throw error;
  }
};

export const useDevice = (Thing_ID) => {
  const accessToken = useJwtToken();
  return useQuery(
    ['device', Thing_ID],
    () => fetchDevice(accessToken, Thing_ID),
    {
      enabled: !!accessToken && !!Thing_ID,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
      ...REACT_QUERY_LOCAL_DEFAULT_OPTIONS,
    }
  );
};

export const fetchReports = async (accessToken, Thing_ID) => {
  const url = new URL(`${API_ENDPOINT}/devices/${Thing_ID}/reports`);
  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: accessToken,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        `HTTP error! status: ${response.status} - ${error.message}`
      );
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching reports list:', error);
    throw error;
  }
};

export const useReports = (Thing_ID) => {
  const accessToken = useJwtToken();
  return useQuery(
    ['reports', Thing_ID],
    () => fetchReports(accessToken, Thing_ID),
    {
      enabled: !!accessToken,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
      ...REACT_QUERY_LOCAL_DEFAULT_OPTIONS,
    }
  );
};

export const fetchStats = async (accessToken, queryParams = {}) => {
  const {
    Thing_ID,
    deviceType,
    reportType,
    deviceSN,
    timestamps,
    lastEvaluatedKey,
  } = queryParams;
  const url = new URL(`${API_ENDPOINT}/devices/${Thing_ID}/stats`);
  url.searchParams.append('deviceType', deviceType);
  if (reportType) {
    url.searchParams.append('reportType', reportType);
  }
  if (deviceSN) {
    url.searchParams.append('deviceSN', deviceSN);
  }
  if (timestamps) {
    const { startTimestamp, endTimestamp } = timestamps;
    url.searchParams.append('startTimestamp', startTimestamp);
    url.searchParams.append('endTimestamp', endTimestamp);
  }
  if (lastEvaluatedKey) {
    url.searchParams.append('lastEvaluatedKey', lastEvaluatedKey);
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: accessToken,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        `HTTP error! status: ${response.status} - ${error.message}`
      );
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching last device stat:', error);
    throw error;
  }
};

//what if there are multiples bms's -- we need to filter based off that particular BMS -- matching the serial number
//same for packs
export const useDeviceLastStat = (Thing_ID) => {
  const accessToken = useJwtToken();
  const { data: device, isLoading: deviceLoading } = useDevice(Thing_ID);
  const { SINGLE_BMS_ID } = verifySingleStack(device);
  const deviceType = getDeviceType(device);
  const EMS_SN = device.Config?.data?.EMS_SN || Thing_ID.replace('BG_', '');

  const lastStat = useQuery(
    ['deviceLastStat', Thing_ID],
    () =>
      fetchStats(accessToken, {
        Thing_ID,
        deviceType,
        deviceSN: SINGLE_BMS_ID ?? EMS_SN,
      }),
    {
      enabled: !!accessToken && !!Thing_ID,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
      ...REACT_QUERY_LOCAL_DEFAULT_OPTIONS,
    }
  );

  return {
    ...lastStat,
    isLoading: deviceLoading || lastStat.isLoading,
    SINGLE_BMS_ID,
    deviceType,
  };
};

export const useDeviceStats = (Thing_ID) => {
  const accessToken = useJwtToken();
  const currentDate = dayjs();
  const timestamps = getDayTimestamps(currentDate);
  return useQuery(
    ['deviceStats', Thing_ID],
    async () => {
      let allItems = [];
      let lastEvaluatedKey = null;

      const fetchPage = async (nextToken) => {
        const response = await fetchStats(accessToken, {
          Thing_ID,
          deviceType: 'NEP',
          timestamps,
          ...(nextToken && { lastEvaluatedKey: nextToken }),
        });

        // Append new items to allItems array
        if (response.stats) {
          allItems = [...allItems, ...response.stats];
          lastEvaluatedKey = response.lastEvaluatedKey;
        } else {
          // Handle single item response
          allItems = [...allItems, response];
          lastEvaluatedKey = null;
        }

        // Recursive call if there's more data
        if (lastEvaluatedKey) {
          await fetchPage(lastEvaluatedKey);
        }
      };

      // Start fetching from the first page
      await fetchPage(null);

      return allItems;
    },
    {
      enabled: !!accessToken && !!Thing_ID,
      staleTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true,
      ...REACT_QUERY_LOCAL_DEFAULT_OPTIONS,
    }
  );
};

export const fetchDeviceState = async (accessToken, Thing_ID) => {
  const url = new URL(`${API_ENDPOINT}/devices/${Thing_ID}/latest-state`);
  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: accessToken,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        `HTTP error! status: ${response.status} - ${error.message}`
      );
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching device latest state:', error);
    throw error;
  }
};

export const deviceStateQueryOptions = (accessToken, Thing_ID) => ({
  queryKey: ['deviceState', Thing_ID],
  queryFn: () => fetchDeviceState(accessToken, Thing_ID),
  enabled: !!accessToken && !!Thing_ID,
  staleTime: 5 * 60 * 1000,
  keepPreviousData: true,
  ...REACT_QUERY_LOCAL_DEFAULT_OPTIONS,
});

// Slice the one-shot payload down to the state object the URL points at.
// For single-stack systems the URL only has BMS_ID and that value is actually
// the module's PACK_SN (see helper/index.js useGetIDsFromURL), so we fall back
// to a pack lookup when no master matches.
export const selectStateForRoute = (payload, BMS_ID, Module_ID) => {
  if (!payload) return undefined;

  const findPack = (sn) => {
    for (const master of payload.masters ?? []) {
      const pack = (master.packs ?? []).find((p) => p.device_sn === sn);
      if (pack) return pack.state ?? null;
    }
    return null;
  };

  if (Module_ID) return findPack(Module_ID);

  if (BMS_ID) {
    const master = (payload.masters ?? []).find(
      (m) => m.device_sn === BMS_ID
    );
    if (master) return master.state ?? null;
    return findPack(BMS_ID);
  }

  return payload.ems?.state ?? null;
};

export const useDeviceState = () => {
  const accessToken = useJwtToken();
  const { Thing_ID, BMS_ID, Module_ID } = useGetIDsFromURL();
  const { isLoading: deviceLoading } = useDevice(Thing_ID);
  const deviceState = useQuery({
    ...deviceStateQueryOptions(accessToken, Thing_ID),
    select: (payload) => selectStateForRoute(payload, BMS_ID, Module_ID),
  });
  return {
    ...deviceState,
    isLoading: deviceLoading || deviceState.isLoading,
  };
};
